// Shared styled-XLSX export utility (uses xlsx-js-style).
//
// USAGE:
//   import { buildStyledWorkbook, downloadStyledWorkbook } from "../lib/excelExport";
//   const wb = buildStyledWorkbook([
//     {
//       name: "Sheet 1",
//       columns: [
//         { key: "name", header: "Client", type: "text" },
//         { key: "amount", header: "Amount", type: "currency" },
//         { key: "date", header: "Trade Date", type: "date" },
//         { key: "pl", header: "P/L", type: "pl" },         // colour-coded
//         { key: "units", header: "Units", type: "number" },
//       ],
//       rows: [{ name: "...", amount: 1234.5, date: "2026-05-14", pl: -50, units: 10.5 }],
//     },
//   ]);
//   downloadStyledWorkbook(wb, "myfile.xlsx");

import * as XLSXStyle from "xlsx-js-style";

// ── Styles ────────────────────────────────────────────────────────────────
const HEADER_STYLE = {
  font: { name: "Calibri", sz: 11, bold: true, color: { rgb: "FFFFFFFF" } },
  fill: { fgColor: { rgb: "FF0F766E" } },           // teal-700
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
  border: {
    top:    { style: "thin", color: { rgb: "FF0F4F49" } },
    bottom: { style: "thin", color: { rgb: "FF0F4F49" } },
    left:   { style: "thin", color: { rgb: "FF0F4F49" } },
    right:  { style: "thin", color: { rgb: "FF0F4F49" } },
  },
};

const BODY_BORDER = {
  top:    { style: "thin", color: { rgb: "FFE5E7EB" } },
  bottom: { style: "thin", color: { rgb: "FFE5E7EB" } },
  left:   { style: "thin", color: { rgb: "FFE5E7EB" } },
  right:  { style: "thin", color: { rgb: "FFE5E7EB" } },
};

const ZEBRA_FILL = { fgColor: { rgb: "FFF8FAFC" } };  // slate-50
const POS_COLOR = { rgb: "FF15803D" };                // green-700
const NEG_COLOR = { rgb: "FFB91C1C" };                // red-700

// Indian-style number format: 1,25,000.50
const FMT_NUMBER  = "#,##,##0.00";
const FMT_CURRENCY = '"₹"#,##,##0.00';
const FMT_INT     = "#,##,##0";
const FMT_DATE    = "dd mmm yyyy";
const FMT_PERCENT = "0.00%";

// ── Helpers ───────────────────────────────────────────────────────────────
const isDateLike = (v) => {
  if (v instanceof Date) return true;
  if (typeof v !== "string") return false;
  // ISO yyyy-mm-dd / yyyy-mm-ddTHH:MM:SS
  return /^\d{4}-\d{2}-\d{2}/.test(v);
};

const toDate = (v) => {
  if (v instanceof Date) return v;
  if (typeof v === "string") {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const isEmpty = (v) =>
  v === null || v === undefined || v === "" ||
  (typeof v === "number" && isNaN(v));

const charWidth = (s) => Math.min(50, Math.max(12, String(s ?? "").length + 2));

// Compute auto-fit column widths from headers + rows.
const autoColumnWidths = (columns, rows) => columns.map((c) => {
  const headerLen = charWidth(c.header);
  let maxLen = headerLen;
  for (const r of rows) {
    let v = r[c.key];
    if (isEmpty(v)) v = "—";
    else if (c.type === "currency" || c.type === "number" || c.type === "pl") {
      const n = Number(v);
      v = isNaN(n) ? String(v) : n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    } else if (c.type === "date") {
      const d = toDate(v);
      v = d ? d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : String(v);
    }
    const l = charWidth(v);
    if (l > maxLen) maxLen = l;
  }
  return { wch: maxLen };
});

// Convert one row into AOA cells (with type & style).
const cellFor = (col, value, isAltRow) => {
  const baseStyle = {
    font: { name: "Calibri", sz: 10 },
    alignment: { vertical: "center", wrapText: true },
    border: BODY_BORDER,
  };
  if (isAltRow) baseStyle.fill = ZEBRA_FILL;

  if (isEmpty(value)) {
    return {
      v: "—",
      t: "s",
      s: { ...baseStyle, alignment: { ...baseStyle.alignment, horizontal: "center" }, font: { ...baseStyle.font, color: { rgb: "FF94A3B8" } } },
    };
  }

  switch (col.type) {
    case "currency":
    case "number":
    case "pl": {
      const n = Number(value);
      if (isNaN(n)) return { v: String(value), t: "s", s: baseStyle };
      const fmt = col.type === "currency" ? FMT_CURRENCY : (col.format || FMT_NUMBER);
      const s = {
        ...baseStyle,
        alignment: { ...baseStyle.alignment, horizontal: "right" },
        numFmt: fmt,
      };
      if (col.type === "pl") {
        s.font = { ...baseStyle.font, color: n < 0 ? NEG_COLOR : POS_COLOR, bold: true };
      }
      return { v: n, t: "n", s };
    }
    case "percent": {
      const n = Number(value);
      if (isNaN(n)) return { v: String(value), t: "s", s: baseStyle };
      // Caller passes the fraction (0.12 for 12%). If value > 1 we assume it's already a percent and divide.
      const frac = Math.abs(n) > 1 ? n / 100 : n;
      return { v: frac, t: "n", s: { ...baseStyle, alignment: { ...baseStyle.alignment, horizontal: "right" }, numFmt: FMT_PERCENT } };
    }
    case "int": {
      const n = Number(value);
      if (isNaN(n)) return { v: String(value), t: "s", s: baseStyle };
      return { v: n, t: "n", s: { ...baseStyle, alignment: { ...baseStyle.alignment, horizontal: "right" }, numFmt: FMT_INT } };
    }
    case "date": {
      const d = toDate(value);
      if (!d) return { v: String(value), t: "s", s: baseStyle };
      return {
        v: d,
        t: "d",
        s: { ...baseStyle, alignment: { ...baseStyle.alignment, horizontal: "center" }, numFmt: FMT_DATE },
      };
    }
    default: {
      const s = isDateLike(value) && col.type === undefined
        ? { ...baseStyle, alignment: { ...baseStyle.alignment, horizontal: "center" } }
        : baseStyle;
      return { v: String(value), t: "s", s };
    }
  }
};

// Build a styled worksheet from columns + rows.
const buildSheet = (columns, rows) => {
  const aoa = [];
  // Header row
  aoa.push(columns.map((c) => ({ v: c.header, t: "s", s: HEADER_STYLE })));
  // Data rows
  rows.forEach((r, i) => {
    const isAlt = i % 2 === 1;
    aoa.push(columns.map((c) => cellFor(c, r[c.key], isAlt)));
  });
  const ws = XLSXStyle.utils.aoa_to_sheet(aoa, { cellStyles: true });
  ws["!cols"] = autoColumnWidths(columns, rows);
  // Freeze the header row (xlsx-js-style uses Excel's frozen pane via the
  // top-left cell pointer — anchoring to A2 freezes row 1 above the split.
  ws["!views"] = [{ state: "frozen", xSplit: 0, ySplit: 1, topLeftCell: "A2" }];
  ws["!autofilter"] = { ref: XLSXStyle.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: 0, c: columns.length - 1 } }) };
  // Row heights — taller header, comfortable body
  const rh = [{ hpx: 28 }];
  for (let i = 0; i < rows.length; i++) rh.push({ hpx: 20 });
  ws["!rows"] = rh;
  return ws;
};

// ── Public API ───────────────────────────────────────────────────────────

// Heuristic to guess column type from header text when not given.
const inferType = (header, sample) => {
  const h = String(header || "").toLowerCase();
  if (/p\s*\/\s*l|profit|loss|gain/.test(h)) return "pl";
  if (/date|on |as on|trade date|purchase date|sale date/.test(h)) return "date";
  if (/%|percent|return|xirr|cagr/.test(h)) return "percent";
  if (/units|count|qty|quantity|nos/.test(h)) return "number";
  if (/amount|value|cost|price|aum|nav|cash|amt|invested|withdrawn|payout|tds|dividend|book|mandate/.test(h)) return "currency";
  if (typeof sample === "number") return "number";
  return "text";
};

// Inputs:
//   sheets: [{ name, columns?, rows }]  — if `columns` not provided, auto-derive from first row keys.
export function buildStyledWorkbook(sheets) {
  const wb = XLSXStyle.utils.book_new();
  (sheets || []).forEach((sheet) => {
    const rows = sheet.rows || [];
    let columns = sheet.columns;
    if (!columns || !columns.length) {
      const headerKeys = rows[0] ? Object.keys(rows[0]) : [];
      columns = headerKeys.map((k) => ({
        key: k,
        header: k,
        type: inferType(k, rows[0]?.[k]),
      }));
    } else {
      // Fill missing type via inference.
      columns = columns.map((c) => ({
        ...c,
        type: c.type || inferType(c.header || c.key, rows[0]?.[c.key]),
      }));
    }
    const ws = buildSheet(columns, rows);
    // Truncate sheet name to 31 chars (Excel hard limit) + dedupe colon/etc.
    const name = String(sheet.name || "Sheet").replace(/[\[\]\*\/\\\?:]/g, " ").slice(0, 31);
    XLSXStyle.utils.book_append_sheet(wb, ws, name || "Sheet");
  });
  return wb;
}

export function downloadStyledWorkbook(wb, filename) {
  XLSXStyle.writeFile(wb, filename, { cellStyles: true });
}

// Convenience: build + download in one call.
export function exportStyledXlsx(sheets, filename) {
  downloadStyledWorkbook(buildStyledWorkbook(sheets), filename);
}
